import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import './style.scss'
import { notificationsActions } from '../../../redux/actions/notifications'
import { factsheetActions } from '../../../redux/actions/factsheet'
import NotificationItem from './NotificationItem'
import { hasNotificationDetail, notificationDetailRenderers } from './details'
import { resolveFactId, resolveFactSearchTerm } from './details/factLabel'
import { ReactComponent as ChevronLeftIcon } from '../../../assets/img/icons/chevron-left.svg'
import history from '../../../history'
import { propertyNavRoutes } from '../Property/NavigationSidebar/NavigationHelper'
import activityTrackingService from '../../../services/activityTrackingService'
import { trackingEvent } from '../../../constants'
import usePublishGuard from '../Property/PublishAllButton/usePublishGuard'
import { setPublishInfoCanPublish } from '../../../services/publishInfoNotifier'

const NotificationCenter = ({ giataId, publishAllowed = true }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const isOpen = useSelector(state => state.notifications.isOpen)
  const items = useSelector(state => state.notifications.items)
  const blueprint = useSelector(state => state.factsheet.blueprint)
  const { canPublish, publish } = usePublishGuard({ giataId, publishAllowed })

  const [selectedId, setSelectedId] = useState(null)

  // the publish-delay notification only makes sense while publishing is possible.
  useEffect(() => {
    setPublishInfoCanPublish(canPublish)
  }, [canPublish])

  const close = useCallback(() => dispatch(notificationsActions.close()), [dispatch])

  // escape closes the center while it is open.
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        close()
      }
    }
    document.addEventListener('keydown', onKeyDown, false)
    return () => document.removeEventListener('keydown', onKeyDown, false)
  }, [isOpen, close])

  // reset to the list view whenever the center is closed.
  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    activityTrackingService.trackEvent(trackingEvent.openNotificationCenter)
  }, [isOpen])

  const onItemClick = (notification) => {
    activityTrackingService.trackEvent(trackingEvent.clickNotification, {
      type: notification.type,
      severity: notification.severity,
    })
    dispatch(notificationsActions.markRead(notification.id))
    if (hasNotificationDetail(notification.type)) {
      setSelectedId(notification.id)
      // detail views resolve fact labels from the blueprint; lazily load it (a
      // no-op if already present) so labels resolve on non-edit views too.
      if (!blueprint) {
        dispatch(factsheetActions.ensureBlueprint())
      }
    }
  }

  const onBack = () => {
    setSelectedId(null)
  }

  // navigates to a property sub-route and closes the center. without state it
  // doesn't push when we're already on that route: pushing (even to the same
  // path) trips the dirty-factsheet navigation guard and pops the
  // unsaved-changes overlay, and the already-mounted view picks up any pending
  // focus/scroll without a navigation. with state it replaces the current entry
  // instead, so the mounted view still receives the new state.
  const goToPropertyRoute = (route, state) => {
    if (giataId) {
      const path = `/property/${giataId}/${route}`
      if (history.location.pathname !== path) {
        history.push(path, state)
      } else if (state) {
        history.replace(path, state)
      }
    }
    close()
  }

  const goToFactsheet = () => goToPropertyRoute(propertyNavRoutes.editProperty)

  // "review factsheet" takes the user to the factsheet and scrolls it into view
  // (it sits below the property info, so it would otherwise be off-screen).
  const onReview = () => {
    dispatch(factsheetActions.setPendingFactsheetScroll())
    goToFactsheet()
  }

  // clicking a contradicting fact jumps straight to that fact in the factsheet
  // (selects its group, scrolls to it and highlights it). when the blueprint
  // doesn't know the fact, fall back to just scrolling the factsheet into view
  // so the customer still lands on it.
  const onFactSelect = (token) => {
    if (resolveFactSearchTerm(blueprint, token)) {
      dispatch(factsheetActions.setPendingFactFocus(resolveFactId(token)))
    } else {
      dispatch(factsheetActions.setPendingFactsheetScroll())
    }
    goToFactsheet()
  }

  // "review rooms" takes the user to the edit-rooms view.
  const onReviewRooms = () => goToPropertyRoute(propertyNavRoutes.editRooms)

  // "review images" takes the user to where the low-resolution images live: the
  // images view, or edit-rooms (expanding a room holding one, see RoomList).
  const onReviewImages = (target) => {
    const state = target === propertyNavRoutes.editRooms ? { expandLowResolutionRoom: true } : undefined
    goToPropertyRoute(target, state)
  }

  const onPublish = () => {
    if (publish({ source: 'notificationCenter' })) {
      close()
    }
  }

  const renderList = () => (
    <React.Fragment>
      <div className="NotificationCenter-Header">
        <p className="NotificationCenter-Header-Title">{t('notificationCenter.title')}</p>
        <span className="NotificationCenter-Header-ActiveFilter"/>
      </div>
      <div className="NotificationCenter-Divider"/>
      <div className="NotificationCenter-List">
        {items.length === 0
          ? <div className="NotificationCenter-Empty">{t('notificationCenter.empty')}</div>
          : items.map(notification => (
            <React.Fragment key={notification.id}>
              <NotificationItem
                notification={notification}
                onClick={() => onItemClick(notification)}
                t={t}
              />
              <div className="NotificationCenter-Divider"/>
            </React.Fragment>
          ))}
      </div>
    </React.Fragment>
  )

  const renderDetail = (notification) => {
    const DetailComponent = notificationDetailRenderers[notification.type]
    return (
      <React.Fragment>
        <div className="NotificationCenter-Header NotificationCenter-Header--detail" onClick={onBack}>
          <button type="button" className="NotificationCenter-Back"
                  aria-label={t('notificationCenter.detail.back')}>
            <ChevronLeftIcon aria-hidden="true"/>
          </button>
          <p className="NotificationCenter-Header-Title">{t('notificationCenter.detail.title')}</p>
        </div>
        <div className="NotificationCenter-Divider"/>
        <div className="NotificationDetail-Content">
          <DetailComponent
            notification={notification}
            blueprint={blueprint}
            onReview={onReview}
            onFactSelect={onFactSelect}
            onReviewRooms={onReviewRooms}
            onReviewImages={onReviewImages}
            onPublish={onPublish}
            publishDisabled={!canPublish}
            t={t}
          />
        </div>
        <div className="NotificationCenter-Divider"/>
      </React.Fragment>
    )
  }

  if (!isOpen) {
    return null
  }

  const selected = selectedId === null
    ? null
    : items.find(n => n.id === selectedId) ?? null

  return (
    <React.Fragment>
      <div className="NotificationCenter-Overlay" onClick={close}/>
      <div className="NotificationCenter-Panel" role="dialog" aria-label={t('notificationCenter.title')}>
        {selected ? renderDetail(selected) : renderList()}
      </div>
    </React.Fragment>
  )
}

NotificationCenter.propTypes = {
  giataId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  publishAllowed: PropTypes.bool,
}

export default NotificationCenter
